/* eslint-disable indent */
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, DataGrid, ConfirmationModalPromise} from '@components';
import {GreenButton, StyledH3, WhiteButton} from '@styled';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useParams} from 'react-router-dom';
import styled from 'styled-components';
import {Breadcrumb} from 'react-bootstrap';
import {orderBy, get} from 'lodash';
import {sagaActions} from './redux/saga';
import AddEditInputData from './AddEditInputData';

const StyledHeadRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .heading {
  }
  .actions {
  }
`;

const RowActions = ({data, onAction}) => {
  return (
    <div style={{display: 'flex', flexWrap: 'wrap'}}>
      <WhiteButton
        onClick={() => onAction('edit', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-edit mr-1" />
        Edit
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('delete', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-trash mr-1" />
        Delete
      </WhiteButton>
      {/* <WhiteButton
        onClick={() => onAction('duplicate', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-trash mr-1" />
        Duplicate
      </WhiteButton> */}
    </div>
  );
};

const ManageSourceInputTableData = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const inputFieldsData = useSelector(
    (state) => state.calcEngine.sourceInputFields.data
  );

  const remoteUnitsData = useSelector(
    (state) => state.calcEngine.sourceInputFields.units
  );

  const selectedSource = useSelector(
    (state) => state.calcEngine.sourceInputFields.source
  );

  const remoteData = useSelector((state) => state.calcEngine.sourceInputData);
  const isLoading = useSelector(
    (state) => state.calcEngine.sourceInputData.loading
  );
  const [editRow, setEditRow] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const history = useHistory();
  const {sourceId, categoryId} = useParams();

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    sortOn: '',
    sortDir: '',
  });

  useEffect(() => {
    if (
      inputFieldsData.length === 0 ||
      (inputFieldsData.length &&
        inputFieldsData[0].sourceId !== Number(sourceId))
    ) {
      dispatch({
        type: sagaActions.FETCH_INPUT_TABLE_FIELDS,
        payload: {
          sourceId,
          values: {
            sortOn: 'fieldLevel',
            sortDir: 'ASC',
          },
        },
      });
    }
  }, [inputFieldsData, sourceId]);

  useEffect(() => {
    if (
      remoteData.page !== pagination.page ||
      remoteData.pageSize !== pagination.pageSize ||
      remoteData.totalCount !== pagination.totalCount ||
      remoteData.sortOn !== pagination.sortOn ||
      remoteData.sortDir !== pagination.sortDir
    ) {
      setPagination({
        page: remoteData.page,
        pageSize: remoteData.pageSize,
        totalCount: remoteData.totalCount,
        sortOn: remoteData.sortOn,
        sortDir: remoteData.sortDir,
      });
    }
  }, [remoteData]);

  const fetchData = ({
    page = 1,
    pageSize = pagination.pageSize,
    sortOn,
    sortDir,
  }) => {
    dispatch({
      type: sagaActions.FETCH_INPUT_TABLE_DATA,
      payload: {
        sourceId,
        values: {
          page,
          pageSize,
          sortOn,
          sortDir,
        },
      },
    });
  };

  const handleDeleteDataRow = (remove) => {
    dispatch({
      type: sagaActions.REMOVE_INPUT_TABLE_DATA,
      payload: {
        sourceId,
        dataId: remove.id,
        values: {},
      },
    });
  };

  const handleUpdateUnitStatus = (row) => {
    const updateStatusTo = row.status === 'Active' ? 'Inactive' : 'Active';
    dispatch({
      type: sagaActions.UPDATE_UNIT,
      payload: {unitId: row.id, values: {status: updateStatusTo}},
    });
  };

  const handleRowAction = useCallback(
    (action, row) => {
      switch (action) {
        case 'edit': {
          setEditRow(row);
          setShowAddEditModal(true);
          break;
        }
        // case 'duplicate': {
        //   setEditRow({...row, duplicate: true});
        //   setShowAddEditModal(true);
        //   break;
        // }
        case 'sources':
          history.push(`/calc-engine/${row.id}/sources`);
          break;
        case 'delete': {
          ConfirmationModalPromise({
            title: `Are you sure you want to delete data row?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleDeleteDataRow(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }
        case 'status': {
          ConfirmationModalPromise({
            title: `Are you sure you want to make unit [${
              row.status === 'Active' ? 'inactive' : 'active'
            }]?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleUpdateUnitStatus(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }

        default:
          break;
      }
    },
    [editRow, showAddEditModal]
  );

  const handleAddData = () => {
    setShowAddEditModal(true);
  };

  const handleToggleAddEditModal = useCallback(() => {
    setEditRow(null);
    setShowAddEditModal(false);
  }, [editRow, showAddEditModal]);

  const renderAddEditModal = () => {
    return (
      <AddEditInputData
        show={showAddEditModal}
        sourceId={sourceId}
        editRow={editRow}
        toggleModal={handleToggleAddEditModal}
      />
    );
  };

  const renderRowActions = (colProps) => {
    return <RowActions data={colProps} onAction={handleRowAction} />;
  };

  const Columns = React.useMemo(() => {
    if (inputFieldsData.length) {
      return orderBy(inputFieldsData, ['fieldLevel'], ['asc'])
        .map((field) => {
          return {
            accessor: `data.${field.id}`,
            Header: field.fieldName,
            tooltip: true,
            className: 'd-none d-lg-block',
            Cell: ({value}) => {
              const resultVal =
                field.fieldType === 'Unit'
                  ? get(
                      remoteUnitsData.find(
                        (unit) => unit.id === Number(value || '0')
                      ),
                      'name',
                      value
                    )
                  : value;
              return resultVal ? resultVal : `${value}`;
            },
          };
        })
        .concat([
          {
            accessor: 'id',
            Header: '#',
            className: 'grey-column d-none d-lg-block',
            width: 50,
            tooltip: false,
            hidden: true,
          },
          {
            accessor: 'conversionEq',
            Header: 'Unit Conversion',
            className: 'd-none d-lg-block',
            width: 50,
            tooltip: true,
            Cell: ({value}) => {
              const [quantity, operator, nonStandardField] = (
                value || ''
              ).split('.');
              return quantity
                ? `${quantity} ${operator} ${get(
                    inputFieldsData.find(
                      (field) => field.id === Number(nonStandardField || '0')
                    ),
                    'fieldName',
                    nonStandardField
                  )}`
                : '';
            },
          },
          {
            accessor: 'standardUnit',
            Header: 'Standard Unit',
            className: 'd-none d-lg-block',
            width: 50,
            tooltip: true,
            Cell: ({value}) => {
              const foundUnit = remoteUnitsData.find(
                (unit) => unit.id === Number(value || '0')
              );
              return foundUnit ? foundUnit.name : value;
            },
          },
          {
            id: 'actions',
            Header: <div className="d-none d-lg-block">Actions</div>,
            accessor: renderRowActions,
            disableSortBy: true,
            disableFilters: true,
            width: 50,
          },
        ]);
    }
    return [
      {
        accessor: 'id',
        Header: '#',
        className: 'grey-column d-none d-lg-block',
        width: 50,
        tooltip: false,
        hidden: true,
      },
      {
        id: 'actions',
        Header: <div className="d-none d-lg-block">Actions</div>,
        accessor: renderRowActions,
        disableSortBy: true,
        disableFilters: true,
        width: 50,
      },
    ];
  }, [inputFieldsData, pagination, remoteUnitsData]);

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/'}}>
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/calc-engine'}}>
            Source Categories
          </Breadcrumb.Item>
          <Breadcrumb.Item
            linkAs={Link}
            linkProps={{to: `/calc-engine/${categoryId}/sources`}}
          >
            Emission Sources
          </Breadcrumb.Item>
          <Breadcrumb.Item
            linkAs={Link}
            linkProps={{
              to: `/calc-engine/${categoryId}/sources/${sourceId}/input-table-fields`,
            }}
          >
            Input Tables Fields
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Input Table Data</Breadcrumb.Item>
        </Breadcrumb>
        <StyledH3>{t('owner.calcEngine.pageHeading')}</StyledH3>
        <StyledHeadRow>
          <div className="heading">
            {t('owner.calcEngine.inputTableData.pageHeading')}
            {selectedSource ? ` for ${selectedSource.name}` : ''}
          </div>
          <div className="actions">
            <GreenButton className="ml-1" onClick={handleAddData}>
              <i className="fa fa-plus mr-1" />
              {t('owner.calcEngine.inputTableData.addData')}
            </GreenButton>
          </div>
        </StyledHeadRow>
        <DataGrid
          columns={Columns}
          data={remoteData.data}
          onFetchData={fetchData}
          pagination={pagination}
          filtering={false}
          expandEnabled={false}
        />
      </div>
      {renderAddEditModal()}
    </>
  );
};

export default ManageSourceInputTableData;
